import { useAuth0 } from '@auth0/auth0-react'
import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  Loader,
  Banner,
  Breadcrumbs,
  Button,
  Input,
  Label,
  LayerCard,
  Select,
  SensitiveInput,
  useKumoToastManager,
} from '@cloudflare/kumo'
import { HouseIcon, WifiHighIcon } from '@phosphor-icons/react'
import { apiRequest, ApiError } from '../api/client'
import { useTranslation } from '../i18n'
import { PageHeader } from '../components/kumo/page-header/page-header'
import type { WifiNetwork, WifiSecurityType, WifiNetworkType } from '@hub-wifi/shared'

interface WifiNetworkFormData {
  name: string
  vlanId: number
  securityType: WifiSecurityType
  networkType: WifiNetworkType
  password: string
}

const emptyForm: WifiNetworkFormData = {
  name: '',
  vlanId: 0,
  securityType: 'WPA2',
  networkType: 'internal',
  password: '',
}

// Maps enum values to i18n keys — mirrors the PrinterStatusBadge pattern
// (single source of truth for enum -> label, shared by the Select options
// here and the badges on WifiNetworksPage).
const NETWORK_TYPE_KEY: Record<WifiNetworkType, string> = {
  internal: 'wifiNetworks.networkType.internal',
  coworkers: 'wifiNetworks.networkType.coworkers',
  guests: 'wifiNetworks.networkType.guests',
}

const SECURITY_TYPE_KEY: Record<WifiSecurityType, string> = {
  Open: 'wifiNetworks.securityType.open',
  WPA2: 'wifiNetworks.securityType.wpa2',
  'WPA2/WPA3 Enterprise': 'wifiNetworks.securityType.wpa2wpa3Enterprise',
}

export function WifiNetworkFormPage() {
  const { id } = useParams<{ id: string }>()
  const isEdit = Boolean(id)
  const navigate = useNavigate()
  const { getAccessTokenSilently } = useAuth0()
  const { t } = useTranslation()
  const toasts = useKumoToastManager()

  const [form, setForm] = useState<WifiNetworkFormData>(emptyForm)
  const [loading, setLoading] = useState(isEdit)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!id) return

    async function fetchNetwork() {
      try {
        const token = await getAccessTokenSilently()
        const network = await apiRequest<WifiNetwork>(`/api/wifi-networks/${id}`, { token })
        setForm({
          name: network.name,
          vlanId: network.vlanId,
          securityType: network.securityType,
          networkType: network.networkType,
          password: network.password ?? '',
        })
      } catch (err) {
        setError(err instanceof ApiError ? err.message : t('common.error'))
      } finally {
        setLoading(false)
      }
    }

    void fetchNetwork()
  }, [id, getAccessTokenSilently, t])

  function setField<K extends keyof WifiNetworkFormData>(key: K, value: WifiNetworkFormData[K]) {
    setForm((prev) => ({ ...prev, [key]: value }))
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setSubmitting(true)

    try {
      const token = await getAccessTokenSilently()

      if (isEdit) {
        await apiRequest(`/api/wifi-networks/${id}`, {
          method: 'PUT',
          token,
          body: form,
        })
        toasts.add({ variant: 'success', title: t('wifiNetworks.updateSuccess') })
      } else {
        await apiRequest('/api/wifi-networks', {
          method: 'POST',
          token,
          body: form,
        })
        toasts.add({ variant: 'success', title: t('wifiNetworks.createSuccess') })
      }
      navigate('/wifi-networks')
    } catch (err) {
      toasts.add({
        variant: 'error',
        title: t('common.errorTitle'),
        description: err instanceof ApiError ? err.message : t('common.error'),
      })
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div>
      <PageHeader
        breadcrumbs={
          <Breadcrumbs>
            <Breadcrumbs.Link icon={<HouseIcon size={14} />} href="/">
              {t('nav.home')}
            </Breadcrumbs.Link>
            <Breadcrumbs.Separator />
            <Breadcrumbs.Link icon={<WifiHighIcon size={14} />} href="/wifi-networks">
              {t('wifiNetworks.title')}
            </Breadcrumbs.Link>
            <Breadcrumbs.Separator />
            <Breadcrumbs.Current>
              {isEdit ? t('wifiNetworks.editNetwork') : t('wifiNetworks.createNetwork')}
            </Breadcrumbs.Current>
          </Breadcrumbs>
        }
        title={isEdit ? t('wifiNetworks.editNetwork') : t('wifiNetworks.createNetwork')}
        description={t('wifiNetworks.pageDescription')}
      />

      {loading && (
        <div className="flex items-center justify-center py-16">
          <Loader size="lg" />
        </div>
      )}

      {!loading && error && (
        <Banner variant="error">
          {t('common.errorPrefix')} {error}
        </Banner>
      )}

      {!loading && !error && (
        <LayerCard>
          <LayerCard.Primary>
            <form onSubmit={(e) => void handleSubmit(e)} className="flex flex-col gap-4">
              <div className="flex flex-col gap-1">
                <Label>{t('wifiNetworks.name')}</Label>
                <Input
                  value={form.name}
                  onChange={(e) => setField('name', e.target.value)}
                  required
                />
              </div>

              <div className="flex flex-col gap-1">
                <Label>{t('wifiNetworks.vlanId')}</Label>
                <Input
                  type="number"
                  value={String(form.vlanId)}
                  onChange={(e) => setField('vlanId', Number(e.target.value))}
                  required
                />
              </div>

              <div className="flex flex-col gap-1">
                <Label>{t('wifiNetworks.securityType')}</Label>
                <Select
                  value={form.securityType}
                  onValueChange={(v) => {
                    if (v) {
                      setField('securityType', v as WifiSecurityType)
                      if (v !== 'WPA2') setField('password', '')
                    }
                  }}
                >
                  <Select.Option value="Open">{t(SECURITY_TYPE_KEY.Open)}</Select.Option>
                  <Select.Option value="WPA2">{t(SECURITY_TYPE_KEY.WPA2)}</Select.Option>
                  <Select.Option value="WPA2/WPA3 Enterprise">
                    {t(SECURITY_TYPE_KEY['WPA2/WPA3 Enterprise'])}
                  </Select.Option>
                </Select>
              </div>

              <div className="flex flex-col gap-1">
                <Label>{t('wifiNetworks.networkType')}</Label>
                <Select
                  value={form.networkType}
                  onValueChange={(v) => {
                    if (v) setField('networkType', v as WifiNetworkType)
                  }}
                >
                  <Select.Option value="internal">{t(NETWORK_TYPE_KEY.internal)}</Select.Option>
                  <Select.Option value="coworkers">{t(NETWORK_TYPE_KEY.coworkers)}</Select.Option>
                  <Select.Option value="guests">{t(NETWORK_TYPE_KEY.guests)}</Select.Option>
                </Select>
              </div>

              {form.securityType === 'WPA2' && (
                <SensitiveInput
                  label={t('wifiNetworks.password')}
                  value={form.password}
                  onValueChange={(value) => setField('password', value)}
                />
              )}

              <div className="flex justify-end gap-2 pt-2">
                <Button
                  variant="secondary"
                  type="button"
                  onClick={() => navigate('/wifi-networks')}
                >
                  {t('wifiNetworks.cancel')}
                </Button>
                <Button variant="primary" type="submit" loading={submitting}>
                  {t('wifiNetworks.save')}
                </Button>
              </div>
            </form>
          </LayerCard.Primary>
        </LayerCard>
      )}
    </div>
  )
}
